/**
 * Per-campaign email sender fields (From email / From name / Reply-To).
 *
 * The From mailbox is restricted to the authenticated `xopnetworks.com` domain —
 * the only domain with SPF/DKIM/DMARC alignment, so an off-domain From would
 * spam-folder or bounce. crm-api enforces the same rule authoritatively
 * (`EmailCampaign#validate_sender_fields`); these client validators just give
 * fast inline feedback. Reply-To may be any valid email (no SPF impact).
 */

/** The default From mailbox when a campaign leaves it blank (mirrors crm-api's
 * `EmailCampaignMailer::CAMPAIGN_FROM_ADDRESS`). */
export const DEFAULT_CAMPAIGN_FROM = "marcom@xopnetworks.com"

/** The only domain a campaign may send FROM. */
export const XOP_EMAIL_DOMAIN = "xopnetworks.com"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const isValidEmail = (v: string) => EMAIL_RE.test(v)

/**
 * Validate the From email. Returns an error string, or `null` when valid.
 * Blank is valid here — it means "use the default" (resolved server-side).
 */
export function validateFromAddress(value: string): string | null {
  const v = value.trim()
  if (!v) return null
  if (!isValidEmail(v)) return "Enter a valid email address"
  // endsWith("@xopnetworks.com") rejects look-alike subdomains
  // (e.g. evil.xopnetworks.com), matching the crm-api check.
  if (!v.toLowerCase().endsWith(`@${XOP_EMAIL_DOMAIN}`)) {
    return `From address must be a ${XOP_EMAIL_DOMAIN} address`
  }
  return null
}

/**
 * Validate the Reply-To email. Returns an error string, or `null` when valid.
 * Blank is valid (Reply-To then follows the From address). Any domain allowed.
 */
export function validateReplyTo(value: string): string | null {
  const v = value.trim()
  if (!v) return null
  if (!isValidEmail(v)) return "Enter a valid email address"
  return null
}
